import { action, computed, extendObservable, makeObservable } from 'mobx'
import { v4 as uuidv4 } from 'uuid'

import { CLASS_NAMES } from '@client/components/constants'
import { BLOCK_TYPES } from '@client/components/Block/constants'
import createBlockModel from '@client/components/Block/createBlockModel'

export default class Column {
  constructor(data = {}, editor) {
    makeObservable(this)
    this.id = uuidv4()
    this.el = null
    this.acceptTypes = data.acceptTypes || []
    this.editor = editor

    extendObservable(this, {
      canDrop: false,
      items: data.items ? data.items.map((item) => createBlockModel(item, this.editor)) : [],
      placeholderPosition: -1,
      widthPersent: data.widthPersent,
    })
  }

  @action amend = (items) => {
    this.items = items.map((item) => createBlockModel(item, this.editor))
  }

  @action clear = () => {
    this.items = []
  }

  @action setCanDrop = (val) => {
    this.canDrop = val
  }

  @action addItemAtTheEnd = (item) => {
    if (!item || item.type === BLOCK_TYPES.NULL) return

    this.items = [
      ...this.items,
      item,
    ]
  }

  @action addItemAt = (item = null, index = 0) => {
    if (!item || item.type === BLOCK_TYPES.NULL) return
    let preparedIndex = index
    if (preparedIndex < 0) {
      preparedIndex = 0
    }
    if (preparedIndex > this.items.length) {
      preparedIndex = this.items.length
    }
    this.items = [
      ...this.items.slice(0, preparedIndex),
      item,
      ...this.items.slice(preparedIndex),
    ]
  }

  @action moveItemAt = (item, index = 0) => {
    if (!item || item.type !== BLOCK_TYPES.SECTION) return
    let preparedIndex = index
    if (preparedIndex < 0) {
      preparedIndex = 0
    }
    if (preparedIndex > this.items.length - 1) {
      preparedIndex = this.items.length - 1
    }
    const items = this.items.filter((it) => it.id !== item.id)
    this.items = [
      ...items.slice(0, preparedIndex),
      item,
      ...items.slice(preparedIndex),
    ]
  }

  @action setPlaceholderPosition = (val) => {
    this.placeholderPosition = val
  }

  @action removeItem = (item) => {
    this.items = this.items.filter((it) => it.id !== item.id)
  }

  @action indexOf = (item) => {
    return this.items.indexOf(item)
  }

  typeAllowed(draggedType) {
    return this.acceptTypes.indexOf(draggedType) > -1
  }

  classNames(draggedType) {
    const typeAllowed =
      this.typeAllowed(draggedType) || draggedType === BLOCK_TYPES.SECTION
        ? CLASS_NAMES.TYPE_ALLOWED
        : ''
    const canDrop = this.canDrop ? CLASS_NAMES.CAN_DROP : ''
    const emptyColumn = this.items.length ? '' : CLASS_NAMES.EMPTY_COLUMN
    return `${typeAllowed} ${canDrop} ${emptyColumn}`.trim()
  }

  @computed get allColors() {
    let colors = []

    this.items.forEach((item) => {
      if (item.allColors) {
        colors = [...colors, ...item.allColors]
      }
    })

    return Array.from(new Set(colors.filter(Boolean)))
  }

  serialize = () => {
    return {
      items: this.items
        .filter((item) => item.type !== BLOCK_TYPES.NULL)
        .map((item) => item.serialize())
        .filter(Boolean),
      widthPersent: this.widthPersent,
    }
  }

  restore = (data) => {
    this.amend(data.items)
    this.widthPersent = data.widthPersent
  }
}

export const NULL = new Column()
